import { create } from 'zustand'

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: string
  images?: string[]
  is_tool_call?: boolean
}

export interface ChatSession {
  session_id: string
  chat_type: string
  title: string
  description?: string
  is_pinned?: boolean
  pinned_at?: string
  response_level?: string
  language?: string
  created_at: string
  last_message_at: string
  message_count: number
  kundli_ids: string[]
  kundli_profile_name?: string
}

interface ChatState {
  // Current chat state
  currentSessionId: string | null
  messages: ChatMessage[]
  isStreaming: boolean

  // Sessions cache
  sessions: ChatSession[]
  totalSessions: number
  hasMoreSessions: boolean
  sessionsOffset: number
  isLoadingSessions: boolean
  lastSessionsFetchedAt: number | null

  // Current chat actions
  setCurrentSession: (sessionId: string | null) => void
  setMessages: (messages: ChatMessage[]) => void
  addMessage: (message: ChatMessage) => void
  updateLastMessage: (content: string) => void
  setStreaming: (isStreaming: boolean) => void
  clearMessages: () => void

  // Sessions cache actions
  setSessions: (sessions: ChatSession[], total: number, hasMore: boolean) => void
  appendSessions: (sessions: ChatSession[], hasMore: boolean) => void
  setLoadingSessions: (loading: boolean) => void
  setSessionsOffset: (offset: number) => void
  removeSession: (sessionId: string) => void
  updateSession: (sessionId: string, updates: Partial<ChatSession>) => void
  markSessionsFetched: () => void
  clearSessionsCache: () => void

  // Helpers
  shouldRefetchSessions: () => boolean
}

// Cache validity: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const useChatStore = create<ChatState>((set, get) => ({
  // Current chat state
  currentSessionId: null,
  messages: [],
  isStreaming: false,

  // Sessions cache state
  sessions: [],
  totalSessions: 0,
  hasMoreSessions: true,
  sessionsOffset: 0,
  isLoadingSessions: false,
  lastSessionsFetchedAt: null,

  // Current chat actions
  setCurrentSession: (sessionId) => set({ currentSessionId: sessionId }),

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateLastMessage: (content) =>
    set((state) => {
      const messages = [...state.messages]
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1]
        messages[messages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + content,
        }
      }
      return { messages }
    }),

  setStreaming: (isStreaming) => set({ isStreaming }),

  clearMessages: () => set({ messages: [] }),

  // Sessions cache actions
  setSessions: (sessions, total, hasMore) => set({
    sessions,
    totalSessions: total,
    hasMoreSessions: hasMore,
    sessionsOffset: 0,
  }),

  appendSessions: (newSessions, hasMore) => set((state) => ({
    sessions: [...state.sessions, ...newSessions],
    hasMoreSessions: hasMore,
  })),

  setLoadingSessions: (isLoadingSessions) => set({ isLoadingSessions }),

  setSessionsOffset: (sessionsOffset) => set({ sessionsOffset }),

  removeSession: (sessionId) => set((state) => ({
    sessions: state.sessions.filter(s => s.session_id !== sessionId),
    totalSessions: Math.max(0, state.totalSessions - 1),
  })),

  updateSession: (sessionId, updates) => set((state) => {
    const updatedSessions = state.sessions.map(s =>
      s.session_id === sessionId ? { ...s, ...updates } : s
    );

    // Re-sort sessions: pinned first (by pinned_at desc), then by last_message_at desc
    updatedSessions.sort((a, b) => {
      // Pinned sessions first
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;

      // If both pinned, sort by pinned_at desc
      if (a.is_pinned && b.is_pinned) {
        const aTime = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
        const bTime = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
        return bTime - aTime;
      }

      // For non-pinned, sort by last_message_at desc
      const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });

    return { sessions: updatedSessions };
  }),

  markSessionsFetched: () => set({ lastSessionsFetchedAt: Date.now() }),

  clearSessionsCache: () => set({
    sessions: [],
    totalSessions: 0,
    hasMoreSessions: true,
    sessionsOffset: 0,
    lastSessionsFetchedAt: null,
  }),

  // Helpers
  shouldRefetchSessions: () => {
    const { lastSessionsFetchedAt, isLoadingSessions } = get();
    if (isLoadingSessions) return false;
    if (!lastSessionsFetchedAt) return true;
    return Date.now() - lastSessionsFetchedAt > CACHE_DURATION;
  },
}))
